import {
  Text,
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
  Button,
  Tooltip,
  IconButton,
  VStack,
  Link,
  List,
  ListItem,
} from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useReports } from '@src/modules/regulatory-reporting/context/reports';
import { useRecoilValue } from 'recoil';
import { currentDatabaseSelector, selectedLockYearState } from '@transport-insights/uikit';
import { useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import InfoPane from '@src/components/shared/InfoPane';
import CcdcCoverageIndicator from '@src/components/shared/CcdcCoverageIndicator';
import ReportContainer from '../../components/ReportContainer';
import useRegulatoryReportingApi from '../../api/useRegulatoryReportingApi';

function DataTable({ tableData, definitions, showTableNumber }) {
  const typeKeys = [...new Set(tableData.data.map((x) => x.data.map(({ type }) => type)).flat())];

  let roadType;

  if (tableData.description.includes('All Roads')) {
    roadType = 'All Roads';
  } else if (tableData.description.includes('Rural Roads')) {
    roadType = 'Rural Roads';
  } else if (tableData.description.includes('Urban Roads')) {
    roadType = 'Urban Roads';
  }

  return (
    <Box
      background="white"
      borderRadius="base"
      boxShadow="sm"
      mb={4}
      overflow="hidden"
    >
      {showTableNumber && (
      <Heading size="sm" p={4}>
        Table
        {' '}
        {tableData.number}
        {'. '}
        {tableData.description}
      </Heading>
      )}
      {!showTableNumber && (
      <VStack align="start" p={4} spacing={0}>
        {roadType && (
        <Text fontSize="xl" fontWeight="bold">
          {roadType}
        </Text>
        )}
        <Heading size="sm" fontWeight="normal">
          {tableData.description}
        </Heading>
      </VStack>
      )}
      <Table size="sm" sx={{ tableLayout: 'fixed', width: '100%' }}>
        <Thead borderTop="1px solid" borderColor="gray.100">
          <Tr>
            <Th>&nbsp;</Th>
            {typeKeys.map((type) => (
              <Th key={type} textAlign="right">
                {type}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {tableData.data.map((category) => {
            const isLastRow = tableData.data[tableData.data.length - 1].category === category.category;
            return (
              <Tr
                key={category.category}
                fontWeight={isLastRow ? 'bold' : 'normal'}
                borderTop={isLastRow ? '2px solid' : 'inherit'}
                borderColor={isLastRow ? 'gray.300' : 'inherit'}
              >
                <Td>{category.category}</Td>
                {typeKeys.map((type) => {
                  const typeData = category.data.find((x) => x.type === type);
                  const isPercentage = typeData?.value?.includes('%');
                  return (
                    <Td
                      key={type}
                      textAlign="right"
                    >
                      {typeData && (
                        <>
                          <Box
                            fontWeight="inherit"
                            fontSize={isPercentage && isLastRow ? '2xl' : 'inherit'}
                            color={isPercentage && isLastRow ? 'brand.orange.500' : 'inherit'}
                          >
                            {typeData.helpRef && (
                              <Popover>
                                <PopoverTrigger>
                                  <Button variant="ghost" colorScheme="gray" p={0} size="xs" mr={2} borderRadius="full">
                                    <Text
                                      as="span"
                                      fontSize="xs"
                                      color="gray.500"
                                      fontWeight="normal"
                                    >
                                      {typeData.helpRef}
                                      .
                                    </Text>
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent _focus={{ boxShadow: 'md' }}>
                                  <PopoverArrow />
                                  <PopoverBody textAlign="left">
                                    <Text
                                      as="span"
                                      fontSize="sm"
                                      color="gray.500"
                                      textAlign="left"
                                      fontWeight="normal"
                                    >
                                      {definitions.find((x) => x.helpRef === typeData.helpRef)?.definition}
                                    </Text>
                                  </PopoverBody>
                                </PopoverContent>
                              </Popover>
                            )}
                            {typeData.value}
                          </Box>
                          {typeData.additionalText && (
                          <Text fontSize="xs" color="gray.500" fontWeight="normal">
                            {typeData.additionalText}
                          </Text>
                          )}
                        </>
                      )}
                    </Td>
                  );
                })}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
}

export default function SmoothTravelExposure() {
  const { data: report, isLoading: isLoadingReport, error } = useReports('ste');
  const basicFactsTables = report?.tables?.filter((table) => table.number <= 6);
  const percentageTables = report?.tables?.filter((table) => table.number > 6).reverse();
  const api = useRegulatoryReportingApi();
  const currentDatabase = useRecoilValue(currentDatabaseSelector);
  const lockYear = useRecoilValue(selectedLockYearState);
  const [isDownloading, setIsDownloading] = useState(false);

  const rcaId = currentDatabase?.id;
  const rcaName = currentDatabase?.name;

  const downloadFile = async () => {
    const fileFormat = 'xlsx';
    const convertedLockYear = lockYear.replace('/', '-');

    const fileName = `Smooth Travel Exposure Report - ${rcaName} (${convertedLockYear}).${fileFormat}`;

    setIsDownloading(true);

    try {
      const res = await api.downloadReport('ste', rcaId, convertedLockYear, fileFormat);

      const blob = new Blob(
        [res],
        {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      );

      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download report:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <ReportContainer
      title="Smooth Travel Exposure"
      isLoading={isLoadingReport}
      noData={isEmpty(report?.tables)}
      downloadButton={(
        <Tooltip label="Download Report Spreadsheet" placement="left">
          <IconButton
            icon={<FiDownload />}
            borderRadius="full"
            colorScheme="blue"
            variant="outline"
            ml={3}
            isDisabled={isEmpty(report?.tables)}
            onClick={() => downloadFile()}
            isLoading={isDownloading}
          />
        </Tooltip>
      )}
      moreInfo={(
        <>
          <Text mb={4}>
            Smooth Travel Exposure (STE) is a key performance measure used to assess the proportion of travel occurring on roads with acceptable smoothness. The measure relies on two key datasets: road condition and traffic estimates.
          </Text>
          <Text mb={4}>
            The Consistent Condition Data Collection (CCDC) project has established a standardised approach for collecting road condition data across Road Controlling Authorities (RCAs). This change supports consistent and reliable reporting of Smooth Travel Exposure.
          </Text>
          <Text mb={4}>
            Transport Insights provides a Traffic Count Estimation (TCE) module that calculates traffic estimates across an RCA’s road network. TCE calculates new Average Daily Traffic (ADT) estimates and traffic loading (a.k.a. traffic mix) for all locally owned road sections in your network using the traffic counts from the last six years and the current estimates.
          </Text>
          <Heading as="h3" size="sm" mb={2}>
            Useful Links
          </Heading>
          <List spacing={2} mb={4}>
            <ListItem>
              <Link href="/Home/HelpLink/RegulatorySTE" isExternal color="blue.500">
                Smooth Travel Exposure Help
              </Link>
            </ListItem>
            <ListItem>
              <Link href="https://www.nzta.govt.nz/partners/planning-and-investment/planning/road-efficiency-group/consistent-condition-data-collection" isExternal color="blue.500">
                Consistent Condition Data Collection
              </Link>
            </ListItem>
            <ListItem>
              <Link href="https://www.nzta.govt.nz/assets/planning-and-investment/docs/consistent-condition-data-collection-smooth-travel-exposure-update-september-2025.pdf" isExternal color="blue.500">
                Smooth Travel Exposure update September 2025
              </Link>
            </ListItem>
          </List>
          <CcdcCoverageIndicator />
        </>
      )}
      error={error}
    >
      <InfoPane mt={0}>
        <Text>
          The information has been extracted from the Carriageway (carr_way) table in AWM.
        </Text>
        <Text>
          The length data and the traffic data have been taken from the Carriageway (carr_way ) table in AWM. Roughness data has been extracted from the Roughness (rough) table using latest roughness values summarised to 100m road sections.
        </Text>
        <Text>
          NB: The figures are rounded and therefore some totals may differ by 1 or 2 because of this. Also, each roughness reading is used in the carriageway section that the end displacement of the reading falls into.
        </Text>
      </InfoPane>
      {percentageTables?.map((table) => (
        <DataTable
          key={table.number}
          tableData={table}
          definitions={report.definitions}
          showTableNumber={false}
        />
      ))}
      <Text>
        Note: This data has been extracted from all Carriageway Sections owned by Local Authority and excludes the following event codes: detour route/temporary alignment/change from nominated lane (D),) works (Road works, marking, etc) (W),  surface contamination (J), wet road (N), sensor drop outs exceed 5% of the number of samples for each reporting interval (C), unsealed road (U), speed outside the limits identified in the quality plan (S), discrepancy in length/locational referencing (L), or data invalid for any other reasons (Z).
      </Text>
      <Heading size="lg" my={4}>
        Basic Facts
      </Heading>
      {basicFactsTables?.map((table) => (
        <DataTable
          key={table.number}
          tableData={table}
          definitions={report.definitions}
          showTableNumber
        />
      ))}
    </ReportContainer>
  );
}
